import { useState, useCallback } from "react";
import { fetchOrderProducts, OrderProduct } from "./useOrderProducts";

export interface BulkOrderEntry {
  voNumber: string;
  products: OrderProduct[];
  message: string;
  type: string;
  status: string;
  isLoading: boolean;
  error: string | null;
}

export const useBulkOrderProducts = () => {
  const [entries, setEntries] = useState<BulkOrderEntry[]>([]);
  const [fetchProgress, setFetchProgress] = useState({ done: 0, total: 0 });
  const [isFetching, setIsFetching] = useState(false);

  const fetchAll = useCallback(async (voNumbers: string[]) => {
    const unique = [...new Set(voNumbers.map(v => v.trim()).filter(Boolean))];
    if (unique.length === 0) return;

    setIsFetching(true);
    setFetchProgress({ done: 0, total: unique.length });

    // Initialize entries
    const initial: BulkOrderEntry[] = unique.map(vo => ({
      voNumber: vo,
      products: [],
      message: "",
      type: "",
      status: "",
      isLoading: true,
      error: null,
    }));
    setEntries(initial);

    // Fetch with concurrency limit of 5
    const concurrency = 5;
    let completed = 0;
    const results = [...initial];

    const fetchOne = async (index: number) => {
      const vo = unique[index];
      try {
        const products = await fetchOrderProducts(vo);
        results[index] = { ...results[index], products, isLoading: false };
      } catch (err: any) {
        results[index] = {
          ...results[index],
          isLoading: false,
          error: err.message || "Ophalen mislukt",
        };
      }
      completed++;
      setFetchProgress({ done: completed, total: unique.length });
      setEntries([...results]);
    };

    // Process in batches of `concurrency`
    for (let i = 0; i < unique.length; i += concurrency) {
      const batch = unique.slice(i, i + concurrency).map((_, j) => fetchOne(i + j));
      await Promise.allSettled(batch);
    }

    setIsFetching(false);
  }, []);

  const updateEntry = useCallback((voNumber: string, updates: Partial<BulkOrderEntry>) => {
    setEntries(prev => prev.map(e => e.voNumber === voNumber ? { ...e, ...updates } : e));
  }, []);

  const updateAllEntries = useCallback((updates: Partial<Pick<BulkOrderEntry, 'message' | 'type' | 'status'>>) => {
    setEntries(prev => prev.map(e => ({ ...e, ...updates })));
  }, []);

  const removeEntry = useCallback((voNumber: string) => {
    setEntries(prev => prev.filter(e => e.voNumber !== voNumber));
  }, []);

  const clearEntries = useCallback(() => {
    setEntries([]);
    setFetchProgress({ done: 0, total: 0 });
  }, []);

  return {
    entries,
    fetchProgress,
    isFetching,
    fetchAll,
    updateEntry,
    updateAllEntries,
    removeEntry,
    clearEntries,
  };
};
